import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef
} from "react"
import { useCurrentUser } from "hooks/use-current-user"

export const PromoCodeContext = createContext()

// PromoCodeProvider is a context provider that sets and removes promo codes
// from url params and localStorage.
//
// If a user provides a promo_code in URL params, we'll set that promo code in
// local storage and make it available to app. This is used in places like payment
// forms and upgrade pages.
//
// Without a URL or stored code, an eligible account falls back to its Signals
// launch offer (window.app.user.signals_launch_offer). That code is never saved,
// so it can't outlive the offer; the server resolves it again on each page load.
export const PromoCodeProvider = ({ children, history = window.history }) => {
  const { signals_launch_offer: launchOffer, refreshSignalsLaunchOffer } =
    useCurrentUser()
  const [explicitPromoCode, setExplicitPromoCode] = useState(null)
  // In some cases, we may want to delay fetching data until the promo code
  // has been retrieved and set. Use this state to handle that case.
  const [promoCodeLoaded, setPromoCodeLoaded] = useState(false)
  const isFirstRender = useRef(true)

  useEffect(() => {
    try {
      const urlPromoCode = new URLSearchParams(window.location.search).get(
        "promo_code"
      )
      if (urlPromoCode) setItem("promo_code", urlPromoCode)

      setExplicitPromoCode(urlPromoCode || getItem("promo_code"))
    } catch (e) {
      console.warn("Error setting promo code", e)
    }
    setPromoCodeLoaded(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    try {
      if (!explicitPromoCode) {
        localStorage.removeItem("promo_code")
        const url = new URL(window.location.href)
        url.searchParams.delete("promo_code")
        history.replaceState({}, document.title, url.href)
      }
    } catch {
      // noop
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explicitPromoCode])

  const launchOfferCode = launchOffer?.code
  const promoCode = explicitPromoCode || launchOfferCode || null
  // Lets consumers tell a code the user brought from one we applied for them,
  // e.g. to skip auto-opening checkout or showing "cannot be applied".
  const isAutoApplied = !explicitPromoCode && Boolean(launchOfferCode)

  // For a code that's done with: dismissed, or just used to upgrade. Re-reads the
  // launch offer too, since an upgrade can change which one the account gets and
  // window.app.user only refreshes on a full page load.
  const removePromoCode = async () => {
    setExplicitPromoCode(null)

    try {
      await refreshSignalsLaunchOffer?.()
    } catch {
      // UserProvider has already cleared the offer rather than keep a stale one.
    }
  }

  return (
    <PromoCodeContext.Provider
      value={{ promoCode, isAutoApplied, removePromoCode, promoCodeLoaded }}
    >
      {children}
    </PromoCodeContext.Provider>
  )
}

const usePromoCode = () => {
  const context = useContext(PromoCodeContext)
  if (!context)
    console.warn("usePromoCode must be used within a PromoCodeProvider")
  return context
}

// Utilities

// Set an item in local storage with an expiry date of 9 days
// If the item is already set and the value is the same, do nothing
//
// Getting a promo code's actual expiry date would require additional
// network calls, so for now, 9 days will do the trick.
export const setItem = (key, value) => {
  try {
    if (getItem(key) === value) return

    const ttl = 777600000 // 9 days
    localStorage.setItem(
      key,
      JSON.stringify({ value, expiry: Date.now() + ttl })
    )
  } catch (e) {
    console.warn("Error setting promo code", e)
  }
}

export const getItem = key => {
  try {
    const item = JSON.parse(localStorage.getItem(key))
    if (item && Date.now() <= item.expiry) return item.value

    localStorage.removeItem(key)
  } catch (e) {
    console.warn("Error retrieving promo code", e)
  }
  return null
}

export { usePromoCode }
